import React, {useMemo} from 'react';
import styles from './BenefactorPointsAssignmentTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, Link} from '@gravity-ui/uikit';
import {DataTable} from '../../../../shared';
import {PointsAssignmentModel} from '../../model';
import {useInfiniteScroll} from '../../../../shared/hooks/useInfiniteScroll';
import {formatDate} from 'date-fns';

interface BenefactorPointsAssignmentTableProps {
  assignments: PointsAssignmentModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  benefactorId: string;
}

export const BenefactorPointsAssignmentTable = ({
  assignments,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  benefactorId,
}: BenefactorPointsAssignmentTableProps) => {
  const SKELETON_ROWS = 5;

  const getReasonText = (item: PointsAssignmentModel) => {
    switch (item.reason) {
      case 'Donation':
        return 'Платеж';
      case 'Unknown':
        return 'Неизвестно';
      default:
        return item.reason;
    }
  };

  const columns = useMemo<TableColumnConfig<PointsAssignmentModel>[]>(
    () => [
      {
        id: 'createdAt',
        name: 'Дата выдачи',

        template: (item: PointsAssignmentModel) => (
          <div className={styles.cellContainer}>
            <Text variant="body-1">{formatDate(item.createdAt, 'dd.MM.yyyy HH:mm')}</Text>
          </div>
        ),
      },
      {
        id: 'points',
        name: 'Количество очков',
        width: 180,
        template: (item: PointsAssignmentModel) => (
          <div className={styles.cellContainer}>
            <Text variant="body-1">{item.points.toLocaleString()}</Text>
          </div>
        ),
      },
      {
        id: 'reason',
        name: 'Причина выдачи',
        template: (item: PointsAssignmentModel) => (
          <div className={styles.cellContainer}>
            <Text variant="body-1">{getReasonText(item)}</Text>
          </div>
        ),
      },
      {
        id: 'orderId',
        name: 'Ссылка на заказ',
        template: (item: PointsAssignmentModel) => {
          if (!item.orderId) {
            return (
              <div className={styles.cellContainer}>
                <Text variant="body-1">-</Text>
              </div>
            );
          }
          return (
            <div className={styles.cellContainer}>
              <Link href={`/accounts/benefactors/profile/${benefactorId}/orders/${item.orderId}`} target="_self">
                <Text variant="body-1">Заказ {item.orderId}</Text>
              </Link>
            </div>
          );
        },
      },
    ],
    [benefactorId],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<PointsAssignmentModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={assignments || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="История выдачи очков пуста"
        emptyStateDescription="У этого благотворителя пока нет истории выдачи очков"
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
